import React, { useState, useEffect, useCallback } from 'react';
import { connect, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Row, Col, Card, CardBody, CardTitle, Alert, Badge, Button, Spinner,
  Modal, ModalHeader, ModalBody,
} from 'reactstrap';
import CloseIcon from 'mdi-react/CloseIcon';
import config from '../../../../../config';
import TableBuilder from '../../../../../shared/components/table/TableBuilder';
import { isDarkTheme } from '../../../../../shared/helpers/themeTableStyles';
import Panel from '../../../../../shared/components/Panel';

const getCardStyle = darkMode => ({
  height: '100%',
  border: darkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
  borderRadius: '6px',
  boxShadow: darkMode ? '0 2px 8px rgba(0, 0, 0, 0.4)' : '0 2px 8px rgba(0, 0, 0, 0.08)',
  backgroundColor: darkMode ? '#232329' : '#ffffff',
  color: darkMode ? '#dddddd' : '#212529',
});

const PLATFORM_LABELS = {
  amazon: 'Amazon',
  amc: 'AMC',
  dsp: 'DSP',
  walmart: 'Walmart',
  google: 'Google',
  meta: 'Meta',
  tiktok: 'TikTok',
  target: 'Target',
  microsoft: 'Microsoft',
  linkedin: 'LinkedIn',
  shopify: 'Shopify',
};

const PLATFORM_ORDER = [
  'amazon', 'amc', 'dsp', 'walmart', 'google', 'meta', 'tiktok', 'target', 'microsoft', 'linkedin', 'shopify',
];

// Status color convention (see notes/API_STATUS_DASHBOARD_GUIDE.md):
// green = configured & successful live test, blue = configured/on but no live test yet,
// gray = not active (missing required setting, or turned off), red = configured/on but the
// live test failed, black = not required/used. `yellow` has been retired.
function colorChange(params) {
  // Check if value exists and is a valid color string
  if (params.value && typeof params.value === 'string' && params.value.trim() !== '') {
    const colorValue = params.value.toLowerCase().trim();
    // Only apply styling for valid color values
    if (['green', 'red', 'blue', 'gray', 'grey', 'black'].includes(colorValue)) {
      return { color: 'white', backgroundColor: colorValue, textAlign: 'center' };
    }
  }
  return { textAlign: 'center' };
}

// Column defs per platform, matching the shapes returned by
// GET /accounts/api-status/all-active?platform=<platform>
const PLATFORM_COLUMN_DEFS = {
  amazon: [
    { field: 'accountId', headerName: 'Account ID' },
    { field: 'accountName', headerName: 'Name', cellStyle: { textAlign: 'left' } },
    { field: 'accountType', headerName: 'Account Type' },
    { field: 'region', headerName: 'Region' },
    { field: 'adApiStatus', headerName: 'AD API Status', cellStyle: colorChange },
    { field: 'spApiStatus', headerName: 'SP API Status', cellStyle: colorChange },
  ],
  amc: [
    { field: 'accountId', headerName: 'Account ID' },
    { field: 'accountName', headerName: 'Name', cellStyle: { textAlign: 'left' } },
    { field: 'status', headerName: 'Status', cellStyle: colorChange },
    { field: 'instanceId', headerName: 'AMC Instance ID' },
    { field: 'managerAccountId', headerName: 'AMC Manager Account ID' },
  ],
  dsp: [
    { field: 'accountId', headerName: 'Account ID' },
    { field: 'accountName', headerName: 'Name', cellStyle: { textAlign: 'left' } },
    { field: 'status', headerName: 'Status', cellStyle: colorChange },
    { field: 'dspAccountId', headerName: 'DSP Account ID' },
    { field: 'dspAccountName', headerName: 'DSP Account Name' },
  ],
  walmart: [
    { field: 'accountId', headerName: 'Account ID' },
    { field: 'accountName', headerName: 'Name', cellStyle: { textAlign: 'left' } },
    { field: 'advertiserId', headerName: 'Advertiser ID' },
    { field: 'adApiStatus', headerName: 'AD API Status', cellStyle: colorChange },
    { field: 'sellerId', headerName: 'Seller ID' },
    { field: 'sellerIdStatus', headerName: 'Seller ID Status', cellStyle: colorChange },
    { field: 'mpApiStatus', headerName: 'MP API Status', cellStyle: colorChange },
    { field: 'mpItemsCount', headerName: 'MP Items Count' },
    { field: 'suApiStatus', headerName: 'SU API Status', cellStyle: colorChange },
    { field: 'advertiserIdStatsCount', headerName: 'Campaign Stats Count' },
  ],
  google: [
    { field: 'accountId', headerName: 'Account ID' },
    { field: 'accountName', headerName: 'Name', cellStyle: { textAlign: 'left' } },
    { field: 'status', headerName: 'Status', cellStyle: colorChange },
    { field: 'googleCustomerId', headerName: 'Google Customer ID' },
    { field: 'googleManagerCustomerId', headerName: 'Manager Customer ID' },
    { field: 'googleMerchantCenterId', headerName: 'Merchant Center ID' },
    { field: 'googleManagerMerchantCenterId', headerName: 'Manager Merchant Center ID' },
  ],
  meta: [
    { field: 'accountId', headerName: 'Account ID' },
    { field: 'accountName', headerName: 'Name', cellStyle: { textAlign: 'left' } },
    { field: 'status', headerName: 'Status', cellStyle: colorChange },
    { field: 'metaAdAccountId', headerName: 'Ad Account ID' },
    { field: 'metaPageId', headerName: 'Page ID' },
    { field: 'metaPixelId', headerName: 'Pixel ID' },
    { field: 'metaCatalogId', headerName: 'Catalog ID' },
    { field: 'metaInstagramAccountId', headerName: 'Instagram Account ID' },
    { field: 'metaProductSetId', headerName: 'Product Set ID' },
  ],
  tiktok: [
    { field: 'accountId', headerName: 'Account ID' },
    { field: 'accountName', headerName: 'Name', cellStyle: { textAlign: 'left' } },
    { field: 'status', headerName: 'Status', cellStyle: colorChange },
    { field: 'tiktokAdvertiserId', headerName: 'Advertiser ID' },
    { field: 'tiktokBusinessCenterId', headerName: 'Business Center ID' },
    { field: 'tiktokCatalogId', headerName: 'Catalog ID' },
    { field: 'tiktokPixelId', headerName: 'Pixel ID' },
    { field: 'tiktokProductSetId', headerName: 'Product Set ID' },
  ],
  target: [
    { field: 'accountId', headerName: 'Account ID' },
    { field: 'accountName', headerName: 'Name', cellStyle: { textAlign: 'left' } },
    { field: 'status', headerName: 'Status', cellStyle: colorChange },
    { field: 'targetVendorId', headerName: 'Vendor ID' },
    { field: 'targetRmsAccountId', headerName: 'RMS Account ID' },
    { field: 'targetSupplierId', headerName: 'Supplier ID' },
    { field: 'targetAccountCurrency', headerName: 'Currency' },
    { field: 'targetTimeZone', headerName: 'Time Zone' },
  ],
  microsoft: [
    { field: 'accountId', headerName: 'Account ID' },
    { field: 'accountName', headerName: 'Name', cellStyle: { textAlign: 'left' } },
    { field: 'status', headerName: 'Status', cellStyle: colorChange },
    { field: 'microsoftAccountId', headerName: 'Microsoft Account ID' },
    { field: 'microsoftClientId', headerName: 'Client ID' },
    { field: 'microsoftObjectId', headerName: 'Object ID' },
    { field: 'microsoftTenantId', headerName: 'Tenant ID' },
  ],
  linkedin: [
    { field: 'accountId', headerName: 'Account ID' },
    { field: 'accountName', headerName: 'Name', cellStyle: { textAlign: 'left' } },
    { field: 'status', headerName: 'Status', cellStyle: colorChange },
  ],
  shopify: [
    { field: 'accountId', headerName: 'Account ID' },
    { field: 'accountName', headerName: 'Name', cellStyle: { textAlign: 'left' } },
    { field: 'status', headerName: 'Status', cellStyle: colorChange },
  ],
};

function AcountStatusAll() {
  const user = useSelector(store => store.authentication.user);
  const theme = useSelector(store => store.theme);
  const darkMode = isDarkTheme(theme);
  const cardStyle = getCardStyle(darkMode);
  const [warnings, setWarnings] = useState([]);
  const [platforms, setPlatforms] = useState({});
  const [loading, setLoading] = useState(true);
  const [checkingPlatform, setCheckingPlatform] = useState(null);
  const [activePlatform, setActivePlatform] = useState(null);
  const [accountsData, setAccountsData] = useState([]);
  const [resultsModalOpen, setResultsModalOpen] = useState(false);

  // Fetch the lightweight platforms overview once on mount.
  useEffect(() => {
    const fetchPlatforms = async () => {
      setLoading(true);
      try {
        const headerWithAuth = {
          ...config.jsonHeader,
          authorization: `JWT ${user.jwtToken}`,
        };
        const response = await fetch(`${config.serverUrl}/accounts/api-status/platforms`, {
          method: 'GET',
          headers: headerWithAuth,
        });
        const data = await response.json();

        if (data.warnings && Array.isArray(data.warnings)) {
          setWarnings(data.warnings);
        }

        if (data.platforms) {
          setPlatforms(data.platforms);
        }
      } catch (error) {
        console.error('Error fetching platforms overview:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPlatforms();
  }, [user.jwtToken]);

  const checkPlatformStatus = useCallback(async (platformKey) => {
    setCheckingPlatform(platformKey);
    setActivePlatform(platformKey);
    setAccountsData([]);
    setResultsModalOpen(true);
    try {
      const headerWithAuth = {
        ...config.jsonHeader,
        authorization: `JWT ${user.jwtToken}`,
      };
      const accountIds = (platforms[platformKey]?.accounts || []).map(a => a.accountId).join(',');
      const url = new URL(`${config.serverUrl}/accounts/api-status/all-active`);
      url.searchParams.set('platform', platformKey);
      if (accountIds) {
        url.searchParams.set('accountIds', accountIds);
      }
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: headerWithAuth,
      });
      const data = await response.json();
      setAccountsData(Array.isArray(data.accounts) ? data.accounts : []);
    } catch (error) {
      console.error(`Error checking status for platform ${platformKey}:`, error);
      setAccountsData([]);
    } finally {
      setCheckingPlatform(null);
    }
  }, [platforms, user.jwtToken]);

  const columnDefs = activePlatform ? PLATFORM_COLUMN_DEFS[activePlatform] : [];

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
  };

  return (
    <>
      <Row>
        <Panel lg={12} title="Platforms" parentRefresh={loading} showRefreshButton={false}>
          <Row>
            {PLATFORM_ORDER.map((platformKey) => {
              const platform = platforms[platformKey] || { accounts: [] };
              const accountCount = platform.accounts ? platform.accounts.length : 0;
              return (
                <Col md={6} lg={4} xl={3} key={platformKey} className="mb-3">
                  <Card style={cardStyle}>
                    <CardBody>
                      <CardTitle tag="h5" className="d-flex justify-content-between align-items-center mb-3">
                        <span style={{ fontSize: '1.35rem', fontWeight: 700 }}>{PLATFORM_LABELS[platformKey]}</span>
                        <Badge
                          color={accountCount > 0 ? 'success' : 'secondary'}
                          style={{ fontSize: '1rem', padding: '0.45em 0.75em' }}
                        >
                          {accountCount}
                        </Badge>
                      </CardTitle>
                      <div style={{ minHeight: '48px', maxHeight: '100px', overflowY: 'auto' }}>
                        {accountCount === 0 && <small className="text-muted">No active accounts</small>}
                        {accountCount > 0 && (
                        <ul style={{ paddingLeft: '18px', marginBottom: 0 }}>
                          {platform.accounts.map(acct => (
                            <li key={acct.accountId}><small>{acct.accountName}</small></li>
                          ))}
                        </ul>
                        )}
                      </div>
                      <div style={{ position: 'relative' }} className="mt-3">
                        <Button
                          color="primary"
                          size="sm"
                          outline
                          block
                          disabled={loading || accountCount === 0 || checkingPlatform === platformKey}
                          onClick={() => checkPlatformStatus(platformKey)}
                        >
                          {checkingPlatform === platformKey ? (
                            <>
                              <Spinner size="sm" className="mr-2" /> Checking...
                            </>
                          ) : 'Check Status'}
                        </Button>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Panel>
      </Row>
      {warnings.length > 0 && (
        <Row className="mt-4">
          <Col md={12}>
            <Card style={cardStyle}>
              <CardBody>
                <CardTitle tag="h5">API Warnings</CardTitle>
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {warnings.map((warning) => (
                    <Alert color="warning" key={`warning-${warning}`} className="mb-2">
                      <small style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {warning}
                      </small>
                    </Alert>
                  ))}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
      <Modal
        isOpen={resultsModalOpen}
        toggle={() => setResultsModalOpen(false)}
        size="xl"
        className={theme.className}
        style={{ maxWidth: '95vw' }}
      >
        <ModalHeader tag="div" close={<span />}>
          <div className="d-flex align-items-center">
            <button
              type="button"
              className="close"
              onClick={() => setResultsModalOpen(false)}
              aria-label="Close"
              style={{ marginRight: '12px' }}
            >
              <CloseIcon size={20} style={{ color: darkMode ? '#dddddd' : '#1c1c1c' }} />
            </button>
            <span>{activePlatform ? `${PLATFORM_LABELS[activePlatform]} — Live Status` : 'Live Status'}</span>
          </div>
        </ModalHeader>
        <ModalBody style={{ minHeight: '70vh' }}>
          <div className="mb-3">
            <small>
              &nbsp;&nbsp;<span className="status__label-color status__label-color--green" />=Configured and successful live test.
              &nbsp;&nbsp;<span className="status__label-color status__label-color--blue" />=Configured (and on, if applicable), no live test yet.
              &nbsp;&nbsp;<span className="status__label-color status__label-color--gray" />=Not active — missing a required setting, or turned off.
              &nbsp;&nbsp;<span className="status__label-color status__label-color--red" />=Configured/on, but the live test failed.
              &nbsp;&nbsp;<span className="status__label-color status__label-color--black" />=Not required/used/needed.
            </small>
          </div>
          {checkingPlatform === activePlatform ? (
            <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '50vh' }}>
              <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
              <div className="mt-3">Checking {activePlatform ? PLATFORM_LABELS[activePlatform] : ''} status...</div>
            </div>
          ) : activePlatform && (
            <TableBuilder
              key={activePlatform}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowData={accountsData}
              parentControlledLoad
              showDatePicker={false}
            />
          )}
        </ModalBody>
      </Modal>
    </>
  );
}

AcountStatusAll.propTypes = {
  user: PropTypes.shape({
    jwtToken: PropTypes.string.isRequired,
  }).isRequired,
};

const mapStateToProps = (state) => ({
  user: state.authentication.user,
});

export default connect(mapStateToProps)(AcountStatusAll);
